import React, { Fragment, useState, useEffect, CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Clock, User, Target, MoveRight, MoveLeft } from "lucide-react";
import { DynamicIcon, IconName } from 'lucide-react/dynamic';

import { ESimulationKey, ESimulationStageKey } from '../../types/entities.enum';

import { useGetSimulationStageDetails } from '../../hooks/simulations';
import { useCreateConversation } from '../../hooks/conversations';

import { getSimulationColor } from '../../helpers/misc.helper';

export const StageDetails = () => {
    const { simulationKey, stageKey, action } = useParams();

    const navigate = useNavigate();

    const [isProcessing, setIsProcessing] = useState(false);

    const { data: details, isLoading } = useGetSimulationStageDetails(simulationKey ? simulationKey as ESimulationKey : null, stageKey ? stageKey as ESimulationStageKey : null);
    const { mutate: createConversation } = useCreateConversation();

    useEffect(() => {
        if (action !== 'start' && action !== 'continue' && action !== 'retry') {
            navigate(`/simulation/${simulationKey}/stages`);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action]);

    const onCreateConversation = () => {
        if (isProcessing || !simulationKey || !stageKey) return;
        if (action !== 'start' && action !== 'continue' && action !== 'retry') return;

        createConversation({
            payload: {
                simulation_key: simulationKey as ESimulationKey,
                stage_key: stageKey as ESimulationStageKey,
                action,
            },
            setIsProcessing,
        });
    };

    return (
        <div
            className='stage_details__page'
            style={{ '--simulation-theme-color': getSimulationColor(simulationKey ? simulationKey as ESimulationKey : null) } as CSSProperties}
        >
            {isLoading && !details && (
                <div className='d-flex flex-column align-items-center justify-content-center gap-sm'>
                    <div className='list-loader' />
                    <p className='text-medium color--gray-700'>Loading stage details</p>
                </div>
            )}
            {!isLoading && !details && (
                <p className='text-lg text-semibold color--gray-600 text-center'>No stages details found!</p>
            )}
            {!isLoading && !!details && (
                <Fragment>
                    <div className='stage_details__top_sec'>
                        <div className='stage_details__top_sec__icon'>
                            <DynamicIcon name={details.icon as IconName} />
                        </div>
                        <div>
                            <p className='text-3xl text-bold mb-xs'>{details.person}</p>
                            <p className='color--gray-600'>{details.title}</p>
                        </div>
                    </div>

                    <div className='card card--md'>
                        <p className='text-sm text-semibold color--gray-600 mb-lg tracking-wider'>CONVERSATION CONTEXT</p>
                        <div className='stage_details__convo_context'>
                            <div className='d-flex flex-column align-items-center gap-sm'>
                                <User />
                                <p className='text-sm text-semibold text-center'>Personality</p>
                                <p className='text-sm color--gray-500 text-center'>{details.personality}</p>
                            </div>
                            <div className='d-flex flex-column align-items-center gap-sm'>
                                <Target />
                                <p className='text-sm text-semibold text-center'>Pushback Style</p>
                                <p className='text-sm color--gray-500 text-center'>{details.pushback_style}</p>
                            </div>
                            <div className='d-flex flex-column align-items-center gap-sm'>
                                <Clock />
                                <p className='text-sm text-semibold text-center'>Duration</p>
                                <p className='text-sm color--gray-500 text-center'>{details.duration}</p>
                            </div>
                        </div>
                        <div className='stage_details__opening'>
                            <p className='text-sm text-semibold color--gray-600 mb-lg tracking-wider'>THEIR OPENING STATEMENT</p>
                            <p className='text-sm text-italic'>"{details.opening}"</p>
                        </div>
                        <div className='stage_details__objectives'>
                            <p className='text-sm text-semibold color--gray-600 mb-lg tracking-wider'>YOUR OBJECTIVES:</p>
                            <ul>
                                {details.objectives.map((objective) => (
                                    <li key={objective} className='text-sm color--gray-700'>{objective}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Fragment>
            )}

            <div className='stage_details__actions'>
                <Link to={`/simulation/${simulationKey}/stages`} className='link link--md link--primary'>
                    <MoveLeft />
                    <span>Back to Overview</span>
                </Link>
                {!isLoading && !!details && (
                    <button className='btn btn--lg btn--primary' onClick={onCreateConversation}>
                        {
                            isProcessing
                                ? <span>Starting...</span>
                                : <span>{action === 'start' ? 'Start Conversation' : action === 'continue' ? 'Continue Conversation' : 'Retry Conversation'}</span>
                        }
                        {
                            isProcessing
                                ? <span className='btn-loader' />
                                : <MoveRight />
                        }
                    </button>
                )}
            </div>
        </div>
    );
};